import { useCallback, useState } from 'react';
import { getToggleFavouriteMutation } from './ToggleFavouriteMutation.generated';
import sendEventToUmami from '@/client/helpers/trackEvent/sendEventToUmami';
import useToggle from '@/client/hooks/useToggle';

type Args = {
  toastTranslations: Record<string, string>;
  game?: GameFragment;
  isGameFavourite?: boolean;
  revalidateFavoriteGame: (tag: string) => Promise<void>;
};

const useFavouriteToggle = ({
  toastTranslations,
  game,
  isGameFavourite,
  revalidateFavoriteGame,
}: Args) => {
  const [isFavourite, toggleFavourite] = useToggle(isGameFavourite);
  const [isToastOpen, handleToastChange] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleClick = useCallback(async () => {
    if (game) {
      toggleFavourite();
      await getToggleFavouriteMutation({ gameId: game.id }).then(
        async ({ data }) => {
          if (!data?.casinoToggleFavourite?.errors) {
            const favourite =
              data?.casinoToggleFavourite?.favouriteGameNode?.isFavourite;
            setToastMessage(
              toastTranslations[
                favourite
                  ? 'casino.game_is_added_to_favourites'
                  : 'casino.game_is_removed_from_favourites'
              ],
            );
            handleToastChange(true);
            window.dispatchEvent(new CustomEvent('favouriteToggle'));
            revalidateFavoriteGame('favoriteGame');
          }
        },
      );

      sendEventToUmami(
        {
          handle: game.handle,
          status: isFavourite ? 'removed' : 'added',
        },
        'toggleFavorite',
      );
    }
  }, [
    game,
    isFavourite,
    revalidateFavoriteGame,
    toastTranslations,
    toggleFavourite,
  ]);

  return {
    isFavourite,
    toastMessage,
    isToastOpen,
    handleClick,
    handleToastChange,
  };
};

export default useFavouriteToggle;
